import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import EmptyState from '../../components/common/EmptyState';
import LinkForm from './LinkForm';
import LinksHub from './LinksHub';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CopyLinkButton from '../../components/common/CopyLinkButton';
import linksService from '../../api/services/links.service';
import donationMethodsService from '../../api/services/donationMethods.service';
import remindersService from '../../api/services/reminders.service';
import useSignupsStore from '../../store/useSignupsStore';
import useContextStore, { selectCurrentContext, scopeOfContext } from '../../store/useContextStore';
import { showToast } from '../../store/useToastStore';
import { consentFormLinkUrl, groupChatLinkUrl } from '../../utils/portalLinks';
import { IconTrash } from '../../components/icons';
import { PATHS } from '../../routes/navConfig';

const PAGE_SIZE = 10;

/* The page opens on the hub (LinksHub). A card's "View All →" either swaps in
   that section's full list here, under its own heading with "← Back to Links"
   to return, or — for the modules with their own route (Sign-Ups, Donation
   Links, Reminders) — navigates away to them. */
const VIEWS = {
  hub: {
    title: 'Links',
    sub: "Group chat, photo albums, sign-up forms — everything you'd otherwise have to dig up in Compose Email lives here too.",
  },
  basics: { title: 'Class Basics', sub: 'The two links every class has — auto-generated, nothing to set up.' },
  links: { title: 'Links', sub: 'Photo albums, shared docs — anything else parents need a link to.' },
};

export default function LinksPage() {
  const ctx = useContextStore(selectCurrentContext);
  const { signups, signupsLoading, signupsTotal, loadSignups } = useSignupsStore();
  const navigate = useNavigate();

  /* Links live per network — the class selected in the header. */
  const networkId = ctx?.schoolId ? ctx.id : null;
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  /* which section is showing — 'hub' or a VIEWS key */
  const [view, setView] = useState('hub');

  const scope = scopeOfContext(ctx);
  const scopedSignups = signups.filter((s) => s.scope === scope);
  const consentUrl = consentFormLinkUrl(networkId);
  const chatUrl = groupChatLinkUrl(networkId, ctx?.publicURL);

  /* ── donation methods (API) ──
     Read-only here: the hub card just needs the count and the badges. Adding
     and editing happen on the Donation Links page, and coming back remounts
     this page, so the card is up to date again. */
  const [methods, setMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);

  useEffect(() => {
    if (!networkId) {
      setMethods([]);
      return undefined;
    }
    let cancelled = false;
    setMethodsLoading(true);
    donationMethodsService
      .list(networkId, { page: 1, limit: 50 })
      .then((r) => { if (!cancelled) setMethods(r.methods); })
      .catch((err) => {
        if (!cancelled) {
          setMethods([]);
          showToast(err.message);
        }
      })
      .finally(() => { if (!cancelled) setMethodsLoading(false); });
    return () => { cancelled = true; };
  }, [networkId]);

  /* ── reminders (API) ──
     Read-only here too — the Reminders page owns the writing. */
  const [reminders, setReminders] = useState([]);
  const [remindersTotal, setRemindersTotal] = useState(0);
  const [remindersLoading, setRemindersLoading] = useState(false);

  useEffect(() => {
    if (!networkId) {
      setReminders([]);
      setRemindersTotal(0);
      return undefined;
    }
    let cancelled = false;
    setRemindersLoading(true);
    remindersService
      .list(networkId, { page: 1, limit: 100 })
      .then((r) => {
        if (!cancelled) {
          setReminders(r.reminders);
          setRemindersTotal(r.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setReminders([]);
          setRemindersTotal(0);
          showToast(err.message);
        }
      })
      .finally(() => { if (!cancelled) setRemindersLoading(false); });
    return () => { cancelled = true; };
  }, [networkId]);

  /* ── sign-ups (API) ──
     Read-only here: the hub card just needs the count and the first titles.
     The Sign-Ups page owns the list and the writing. */
  useEffect(() => {
    if (!networkId) return;
    loadSignups(networkId, { page: 1, limit: PAGE_SIZE }, scope).catch((err) => showToast(err.message));
  }, [networkId, scope, loadSignups]);

  useEffect(() => {
    setPage(1);
  }, [networkId]);

  useEffect(() => {
    if (!networkId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await linksService.list(networkId, { page, limit: PAGE_SIZE });
        if (!cancelled) {
          setLinks(result.links);
          setTotal(result.total);
          setTotalPages(result.totalPages);
        }
      } catch (err) {
        if (!cancelled) showToast(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId, page, refreshKey]);

  const refresh = ({ toFirstPage = false } = {}) => {
    if (toFirstPage && page !== 1) setPage(1);
    else setRefreshKey((k) => k + 1);
  };

  /* ── hub ⇄ section switching ──
     Sign-Ups, Donation Links and Reminders are their own routes, so opening
     those leaves this page; `withForm` rides along as navigation state so the
     donation / reminder add form is already open on arrival. The rest swap in
     place. */
  const openSection = (key, { withForm = false } = {}) => {
    const state = withForm ? { state: { openForm: true } } : undefined;
    if (key === 'signups') {
      navigate(PATHS.SIGNUPS);
      return;
    }
    /* the Class Basics consent row opens the consent responses, not a list here */
    if (key === 'consent') {
      navigate(PATHS.CONSENT);
      return;
    }
    if (key === 'donations') {
      navigate(PATHS.DONATION_LINKS, state);
      return;
    }
    if (key === 'reminders') {
      navigate(PATHS.REMINDERS, state);
      return;
    }
    setView(key);
    if (key === 'links') {
      setEditingLink(null);
      setFormOpen(withForm);
    }
  };

  const backToHub = () => {
    setView('hub');
    setFormOpen(false);
    setEditingLink(null);
  };

  /* The hub's quick-action tiles. Sign-ups, donation links and reminders each
     have their own page; only Add Link opens a section here. */
  const quickAction = (key) => {
    if (key === 'signup') navigate(PATHS.SIGNUPS, { state: { view: 'create' } });
    else if (key === 'link') openSection('links', { withForm: true });
    else if (key === 'donation') openSection('donations', { withForm: true });
    else if (key === 'reminder') openSection('reminders', { withForm: true });
  };

  /* "Send to parents" hands off to Compose Email with just that one thing
     attached, landing on the preview of what parents will actually get. */
  const sendToParents = (draft) => {
    navigate(PATHS.EMAIL, { state: { linkDraft: draft } });
  };

  /* A link's "Send to parents": the API creates links inactive (hidden in the
     app) until the room parent sends them, so this first makes the link
     visible, then hands off to Compose Email with just that link attached. */
  const [sendBusyId, setSendBusyId] = useState(null);
  const sendLink = async (l) => {
    setSendBusyId(l.id);
    try {
      if (!l.isActive) {
        await linksService.setStatus(l.id, true);
        setLinks((prev) => prev.map((x) => (x.id === l.id ? { ...x, isActive: true } : x)));
      }
      sendToParents({
        subject: `${l.label} — ${ctx.name}`,
        body: `Hi everyone!\n\nSharing a link for our class: ${l.label}. You'll find it below.\n\nThank you!`,
        include: { linkId: l.id },
      });
    } catch (err) {
      showToast(err.message);
    } finally {
      setSendBusyId(null);
    }
  };

  const [linkDeleteTarget, setLinkDeleteTarget] = useState(null);
  const [linkDeleting, setLinkDeleting] = useState(false);
  const confirmDeleteLink = async () => {
    const l = linkDeleteTarget;
    if (!l) return;
    setLinkDeleting(true);
    try {
      await linksService.remove(l.id);
      setLinkDeleteTarget(null);
      showToast('Link deleted ✓');
      /* the page is now short a row — pull it again, stepping back a page when
         that was the last row on it */
      refresh({ toFirstPage: (links || []).length === 1 });
    } catch (err) {
      showToast(err.message);
    } finally {
      setLinkDeleting(false);
    }
  };

  const heading = VIEWS[view] || VIEWS.hub;

  return (
    <div className="sec on">
      <SectionHead
        title={heading.title}
        sub={heading.sub}
        actions={
          view !== 'hub' ? (
            <>
              <button className="btn btn-ghost" onClick={backToHub}>
                ← Back to Links
              </button>
              {view === 'links' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (formOpen && !editingLink) setFormOpen(false);
                    else {
                      setEditingLink(null);
                      setFormOpen(true);
                    }
                  }}
                >
                  + Add Link
                </button>
              )}
            </>
          ) : undefined
        }
      />

      {view === 'hub' && (
        <LinksHub
          ctxName={ctx.name}
          consentUrl={consentUrl}
          chatUrl={chatUrl}
          signups={scopedSignups}
          signupsTotal={signupsTotal}
          signupsLoading={signupsLoading}
          links={links}
          linksTotal={total}
          linksLoading={loading}
          donationMethods={methods}
          donationMethodsLoading={methodsLoading}
          reminders={reminders}
          remindersTotal={remindersTotal}
          remindersLoading={remindersLoading}
          onOpen={(key) => openSection(key)}
          onQuick={quickAction}
        />
      )}

      {view === 'basics' && (
        <>
          <Card title="Consent Form">
            <div className="signup-card">
              <div
                className="thumb"
                style={{ display: 'grid', placeItems: 'center', fontSize: 22, cursor: 'pointer' }}
                title="View parents who consented"
                onClick={() => navigate(PATHS.CONSENT)}
              >
                📋
              </div>
              <div
                className="info"
                style={{ cursor: 'pointer' }}
                title="View parents who consented"
                onClick={() => navigate(PATHS.CONSENT)}
              >
                <div className="t">Parent Consent Form</div>
                <div className="meta">{consentUrl}</div>
              </div>
              <div className="actions">
                <CopyLinkButton url={consentUrl} />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    sendToParents({
                      subject: `Please fill out our class consent form — ${ctx.name}`,
                      body: `Hi everyone!\n\nPlease take a minute to fill out the parent consent form HERE so we have your info for the year. It only takes a minute and it's completely optional.\n\nThank you!`,
                      include: { consent: true },
                    })
                  }
                >
                  Send to parents
                </button>
              </div>
            </div>
          </Card>

          <Card title="Class Group Chat">
            <div className="signup-card">
              <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 22 }}>💬</div>
              <div className="info">
                <div className="t">{ctx.name}</div>
                <div className="meta">{chatUrl}</div>
              </div>
              <div className="actions">
                <CopyLinkButton url={chatUrl} />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    sendToParents({
                      subject: `Join our class group chat — ${ctx.name}`,
                      body: `Hi everyone!\n\nWe've set up a group chat for our class — handy for birthday invitations, play dates, parent social outings, carpools, and quick questions through the year. The link is below, and joining is completely optional.\n\nThank you!`,
                      include: { groupchat: true },
                    })
                  }
                >
                  Send to parents
                </button>
              </div>
            </div>
            <div className="hint">
              Auto-generated for this class — anyone who opens it joins the group chat, no separate invite needed.
            </div>
          </Card>
        </>
      )}

      {view === 'links' && (
        <Card>
          <LinkForm
            open={formOpen}
            editingLink={editingLink}
            networkId={networkId}
            onClose={() => {
              setFormOpen(false);
              setEditingLink(null);
            }}
            onSaved={() => refresh({ toFirstPage: !editingLink })}
          />
          <div>
            {loading && (
              <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
                <Spinner variant="green" /> Loading links…
              </div>
            )}
            {!loading && (!links || links.length === 0) && <EmptyState>No links yet.</EmptyState>}
            {!loading &&
              (links || []).map((l) => (
                <div className="signup-card" key={l.id}>
                  <div className="info">
                    <div className="t">{l.label}</div>
                    <div className="meta">{l.url}</div>
                  </div>
                  <div className="actions">
                    {!l.isActive && (
                      <span className="ev-status draft" style={{ alignSelf: 'center' }}>
                        Not sent yet
                      </span>
                    )}
                    <CopyLinkButton url={l.url} />
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={sendBusyId === l.id}
                      title="Make this link visible to parents and draft the email"
                      onClick={() => sendLink(l)}
                    >
                      {sendBusyId === l.id ? 'Sending…' : 'Send to parents'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setEditingLink(l);
                        setFormOpen(true);
                      }}
                    >
                      Edit
                    </button>
                    <div className="ev-trash" title="Delete link" onClick={() => setLinkDeleteTarget(l)}>
                      <IconTrash />
                    </div>
                  </div>
                </div>
              ))}
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  {total} link{total === 1 ? '' : 's'} · Page {page} of {totalPages}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                    ← Prev
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}


      <ConfirmDialog
        open={!!linkDeleteTarget}
        title="Delete Link"
        confirmLabel="Delete Link"
        busy={linkDeleting}
        onCancel={() => setLinkDeleteTarget(null)}
        onConfirm={confirmDeleteLink}
      >
        {linkDeleteTarget && (
          <>
            <div className="signup-card" style={{ marginBottom: 12 }}>
              <div className="info">
                <div className="t">{linkDeleteTarget.label}</div>
                <div className="meta">{linkDeleteTarget.url}</div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
              This removes the link from the class list and from the app. Emails you already sent still contain
              it. This can't be undone.
            </p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
